import { Component, Input, contentChild } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { getDefaultDateForInhibitionTimeout } from 'libs/utils-common/src';
import { AbsPipe } from 'libs/utils-common/src/lib/pipes/abs.pipe';
import { DeviceData3D } from '../../models/device-data.model';
import { BatteryState } from '../../models/enums/battery-state.enum';
import { DeviceState } from '../../models/enums/device-state.enum';
import { DeviceType3D, DeviceTypeEnum, PanelType } from '../../models/enums/device-type.enum';
import { BatterieIconComponent } from '../batterie-icon/batterie-icon.component';
import { DeviceTypeLabelComponent } from '../device-type-label/device-type-label.component';
import { FadingSpanComponent } from '../fading-span/fading-span.component';
import { BatteryPausedInfoDirective } from './battery-paused-info.directive';

export type InstantPowerDisplay = { value: number | string; suffix: string };

@Component({
    selector: 'web-device-data',
    templateUrl: './device-data.component.html',
    styleUrls: ['./device-data.component.scss'],
    imports: [
        DeviceTypeLabelComponent,
        TranslateModule,
        FadingSpanComponent,
        BatterieIconComponent,
        AbsPipe,
        IonicModule,
    ],
})
export class DeviceDataComponent {
    @Input()
    public device?: DeviceData3D;

    public DeviceType3D = DeviceType3D;

    public PanelType = PanelType;

    DeviceTypeEnum = DeviceTypeEnum;

    public BatteryState = BatteryState;

    /** true when `<ng-content select="[batteryPausedInfo]">` has some content */
    public readonly batteryPausedInfo = contentChild(BatteryPausedInfoDirective);

    public get DeviceState() {
        return DeviceState;
    }

    public get isMeter(): boolean {
        return this.device?.type === DeviceType3D.METER;
    }

    public get hasConsumption(): boolean {
        return this.isMeter && !!(this.device?.hasLoadPhase || this.device?.hasConsumptionDevice);
    }

    public checkNumberAndGreaterThanOrEqualZero(value: number | undefined): boolean {
        return typeof value === 'number' && value >= 0;
    }

    protected isBatteryPaused(): boolean {
        return (
            this.device?.batteryState === BatteryState.OFF &&
            (this.device?.inhibitionTimeout ?? getDefaultDateForInhibitionTimeout()) > new Date()
        );
    }

    canHaveSellBack(): boolean {
        if (this.device?.deviceType === DeviceTypeEnum.STREAM_METER) {
            return true;
        }
        if (
            (this.device?.deviceType === DeviceTypeEnum.ERL || this.device?.deviceType === DeviceTypeEnum.ERL_GEN2) &&
            this.device?.isSellBack === true
        ) {
            return true;
        }
        return false;
    }

    formatInstantPower(power: number | undefined): InstantPowerDisplay {
        if (power === undefined || power === null) {
            return { value: '-', suffix: '' };
        }
        const useKw = Math.abs(power) >= 1000;
        return {
            value: useKw ? Number((power / 1000).toPrecision(2)) : Number(power.toPrecision(2)),
            suffix: useKw ? 'kW' : 'W',
        };
    }
}
